import { memo } from 'react';

import { Flex, Label, Radio, RadioGroup, Text, TextArea, TextField } from '@/components/core';
import { useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { DivProps } from '@/utils';

import { findField } from './utils';

type ValidationFormSettingsProps = DivProps & {
  fieldId: string;
  validatorId: string;
  placeholderMessage?: string;
};

const IValidationFormSettings = ({
  fieldId,
  validatorId,
  placeholderMessage,
  ...rest
}: ValidationFormSettingsProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const validator = field?.validators?.find((v) => v.id === validatorId);

  return (
    <Flex col className="gap-md" {...rest}>
      <Flex col className="gap-sm">
        <Label>If validation fails:</Label>

        <RadioGroup
          onChange={(key) => mutate((f) => (f.config.validationMode = key.toString()))}
          defaultValue="warning"
        >
          <Radio value="warning">
            <Flex col>
              <Text slot="description">Show warning</Text>
              <Text slot="description" variant="secondary">
                Warnings can be ignored by a human reviewer.
              </Text>
            </Flex>
          </Radio>
          <Radio value="error">
            <Flex col>
              <Text slot="description">Show error</Text>
              <Text slot="description" variant="secondary">
                Errors must be resolved before continuing.
              </Text>
            </Flex>
          </Radio>
        </RadioGroup>
      </Flex>

      <TextField
        className="flex w-full flex-col"
        onChange={(value: string) => mutate((f) => (f.config.errorMessage = value))}
        defaultValue={validator?.config?.errorMessage?.toString()}
      >
        <Label>Customize the message</Label>
        <TextArea placeholder={placeholderMessage} />
        <Text slot="description">The error message the user will see in the validation interface.</Text>
      </TextField>
    </Flex>
  );
};

export const ValidationFormSettings = memo(IValidationFormSettings) as typeof IValidationFormSettings;
